import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Document, RootState } from '../../../types/common.types';
import { Collection, Resource } from '../../../types/entity.types';

import { useCollectionApi } from '../../../api/collection/useCollectionApi';
import { useDocumentApi } from '../../../api/document/useDocumentApi';
import { useMemberApi } from '../../../api/member/useMemberApi';
import { useProgrammeApi } from '../../../api/programme/useProgrammeApi';
import { useV2ProgrammeApi } from '../../../api/programme/v2/useV2ProgrammeApi';
import { useResourceApi } from '../../../api/resource/useResourceApi';

import { setParentProgramme, setProgramme, setSubProgramme } from '../../../slices/programme/programme.slice';

import { downloadFile } from '../../../utils/util';

import Plan from '../../../components/screens/programme/plan/Plan';

const PlanContainer = () => {
  const { parentProgramme } = useSelector(({ programme }: RootState) => programme);
  const { collections } = useSelector(({ plan }: RootState) => plan);
  const { members } = useSelector(({ member }: RootState) => member);

  const dispatch = useDispatch();

  const { status, programmeExternalReference } = useParams();

  const [searchParams] = useSearchParams();

  const { viewProgramme } = useProgrammeApi();

  const { viewParentProgramme } = useV2ProgrammeApi();

  const { viewAllMembers } = useMemberApi();

  const {
    createCollection: createCollectionRequest,
    viewAllCollections,
    updateCollection: updateCollectionRequest,
    deleteCollection: deleteCollectionRequest
  } = useCollectionApi();

  const {
    createResource: createResourceRequest,
    updateResource: updateResourceRequest,
    deleteResource: deleteResourceRequest
  } = useResourceApi();

  const { downloadDocument: downloadDocumentRequest, uploadDocument: uploadDocumentRequest } = useDocumentApi();

  useEffect(() => {
    if (!programmeExternalReference) return;

    const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';
    if (!hasSubProgrammes) {
      viewProgramme({ externalReference: programmeExternalReference });

      viewAllCollections({ programmeExternalReference });

      viewAllMembers({ programmeExternalReference });

      dispatch(setParentProgramme(undefined));
      dispatch(setSubProgramme(undefined));
      return;
    }

    viewParentProgramme({ programmeExternalReference }, (data) => {
      if (!data?.subProgrammeSummaries || data.subProgrammeSummaries.length <= 0) return;

      const subExternalReference = data.subProgrammeSummaries[0]?.externalReference;

      if (!subExternalReference) return;

      viewAllCollections({ programmeExternalReference: subExternalReference });

      viewAllMembers({ programmeExternalReference: subExternalReference });

      dispatch(setProgramme(undefined));
    });
  }, [
    programmeExternalReference,
    dispatch,
    searchParams,
    viewParentProgramme,
    viewProgramme,
    viewAllMembers,
    viewAllCollections
  ]);

  const createCollection = (
    collection: Collection,
    onSuccess: (externalReference: string) => void,
    subProgrammeExternalReference?: string
  ) => {
    createCollectionRequest(
      {
        programmeExternalReference: subProgrammeExternalReference ?? programmeExternalReference!,
        collection
      },
      onSuccess
    );
  };

  const updateCollection = (collection: Collection, onSuccess?: () => void, subProgrammeExternalReference?: string) => {
    const externalReference = subProgrammeExternalReference ?? programmeExternalReference;

    const updatedCollection = { ...collection };
    updatedCollection.programmeExternalReference = externalReference;

    updateCollectionRequest(
      {
        collection: updatedCollection
      },
      onSuccess
    );
  };

  const deleteCollection = (externalReference: string, subProgrammeExternalReference?: string) => {
    deleteCollectionRequest({
      programmeExternalReference: subProgrammeExternalReference ?? programmeExternalReference!,
      planExternalReference: externalReference
    });
  };

  const uploadDocument = async (document: Document): Promise<string> => {
    return new Promise((resolve) => {
      uploadDocumentRequest(document, (documentExternalReference) => {
        resolve(documentExternalReference);
      });
    });
  };

  const downloadDocument = (resource: Resource) => {
    const { documentExternalReference, name } = resource;

    if (!documentExternalReference || !name) return;

    downloadDocumentRequest({ documentExternalReference }, (data) => downloadFile(data, name));
  };

  const createResource = async (
    collectionExternalReference: string,
    resource: Resource,
    onSuccess: () => void,
    subProgrammeExternalReference?: string
  ) => {
    if (resource.document) {
      const documentExternalReference = await uploadDocument(resource.document);
      resource = { ...resource, documentExternalReference };
      delete resource.document;
    }

    const externalReference = subProgrammeExternalReference ?? programmeExternalReference;

    createResourceRequest(
      {
        programmeExternalReference: externalReference as string,
        collectionExternalReference,
        resource
      },
      onSuccess
    );
  };

  const updateResource = async (resource: Resource, onSuccess?: () => void, subProgrammeExternalReference?: string) => {
    if (resource.document) {
      const documentExternalReference = await uploadDocument(resource.document);
      resource = { ...resource, documentExternalReference };
      delete resource.document;
    }

    const externalReference = subProgrammeExternalReference ?? programmeExternalReference;

    updateResourceRequest(
      {
        programmeExternalReference: externalReference!,
        collectionExternalReference: resource.collectionExternalReference!,
        resource
      },
      onSuccess
    );
  };

  const deleteResource = (
    collectionExternalReference: string,
    resourceExternalReference: string,
    subProgrammeExternalReference?: string
  ) => {
    deleteResourceRequest({
      programmeExternalReference: subProgrammeExternalReference ?? programmeExternalReference!,
      planExternalReference: collectionExternalReference,
      resourceExternalReference
    });
  };

  const switchSubProgramme = (externalReference: string) => {
    viewAllCollections({ programmeExternalReference: externalReference });
    viewAllMembers({ programmeExternalReference: externalReference });
  };

  const sortCollections = () => {
    const sortedCollections = [...collections].sort((collectionOne, collectionTwo) => {
      const nameOne = collectionOne.name.toLowerCase();
      const nameTwo = collectionTwo.name.toLowerCase();

      if (nameOne > nameTwo) return 1;
      if (nameOne < nameTwo) return -1;
      return 0;
    });

    return sortedCollections;
  };

  return (
    <Plan
      status={status as string}
      collections={sortCollections()}
      totalMembers={members.length}
      parentProgramme={parentProgramme}
      createCollection={createCollection}
      updateCollection={updateCollection}
      deleteCollection={deleteCollection}
      createResource={createResource}
      updateResource={updateResource}
      deleteResource={deleteResource}
      downloadDocument={downloadDocument}
      switchSubProgramme={switchSubProgramme}
    />
  );
};

export default PlanContainer;
